/**
 * 아주 작은 주소 동기화 — 라우팅 라이브러리 없이 History API 만 쓴다.
 *
 * 네 앱 모두 상태(menu/tab/route)로 화면을 고르고, 주소는 그 상태를 **비추는 거울**이다.
 * 화면이 바뀌면 주소를 밀어 넣고(pushState), 뒤로가기(popstate)·새로고침·딥링크는 주소를
 * 읽어 상태를 되살린다. 라이브러리를 쓰지 않는 이유 — 화면이 한 자릿수인 앱 네 개에
 * 중첩 라우트·로더가 필요 없고, 기존 상태 기반 분기를 그대로 두고 얹을 수 있어서다.
 *
 * nginx 는 `/` 와 `/apply/` 모두 index.html 로 fallback 하므로(deploy/nginx.conf)
 * 어떤 경로를 새로고침해도 앱이 뜬다.
 */

import { useCallback, useSyncExternalStore } from 'react';

export interface Location {
  /** `window.location.pathname` (base 포함). */
  path: string;
  search: URLSearchParams;
}

const EVENT = 'solar:navigate';
let snapshot: Location | null = null;

function read(): Location {
  const path = window.location.pathname;
  const search = window.location.search;
  // useSyncExternalStore 는 참조가 같아야 재렌더를 건너뛴다 — 값이 같으면 같은 객체를 돌려준다.
  if (snapshot && snapshot.path === path && snapshot.search.toString() === new URLSearchParams(search).toString()) {
    return snapshot;
  }
  snapshot = { path, search: new URLSearchParams(search) };
  return snapshot;
}

function subscribe(onChange: () => void): () => void {
  window.addEventListener('popstate', onChange);
  window.addEventListener(EVENT, onChange);
  return () => {
    window.removeEventListener('popstate', onChange);
    window.removeEventListener(EVENT, onChange);
  };
}

/** 현재 주소. popstate 와 navigate() 양쪽에 반응한다. */
export function useLocation(): Location {
  return useSyncExternalStore(subscribe, read, read);
}

/**
 * 주소를 바꾼다. 같은 주소면 아무것도 하지 않는다(히스토리에 중복 항목이 쌓이지 않게).
 * `replace` 는 초기 정규화(알 수 없는 경로 → 기본 화면)처럼 뒤로가기로 돌아올 필요가
 * 없는 변경에 쓴다. `state` 는 주소에 넣고 싶지 않은 부가 정보(예: 상세에서 돌아갈 탭).
 */
export function navigate(
  to: string,
  options: { replace?: boolean; state?: unknown } = {},
): void {
  const url = new URL(to, window.location.href);
  const current = window.location.pathname + window.location.search;
  const next = url.pathname + url.search;
  if (next === current && !options.replace) return;
  if (options.replace) window.history.replaceState(options.state ?? null, '', next);
  else window.history.pushState(options.state ?? null, '', next);
  window.dispatchEvent(new Event(EVENT));
}

/** 현재 히스토리 항목의 state (navigate 의 `state`, 또는 popstate 로 되돌아온 값). */
export function historyState(): unknown {
  return window.history.state;
}

/** 쿼리 하나만 바꾼 주소를 만든다. value 가 null 이면 지운다. */
export function withQuery(location: Location, key: string, value: string | null): string {
  const search = new URLSearchParams(location.search);
  if (value === null) search.delete(key);
  else search.set(key, value);
  const qs = search.toString();
  return qs ? `${location.path}?${qs}` : location.path;
}

/** navigate 를 컴포넌트에서 쓰기 편한 형태로. */
export function useNavigate() {
  return useCallback(navigate, []);
}
